import { left, right } from 'fp-ts/Either';

import { httpClientFunction } from 'domain/types';
import Profile from 'domain/entity/profile';
import GetProfileClient from 'use-case/profile/get/getProfileClient';
import { LocationType } from 'domain/entity/location';
import Language from 'domain/entity/language';

export default (httpClient: httpClientFunction, baseUrl: string): GetProfileClient => () =>
    httpClient(`${baseUrl}/users/current/profile`, {})
        .then((response) =>
            response.ok
                ? response
                      .json()
                      .then(asProfile)
                      .then(right)
                      .catch(() => left({ error: new Error(), status: 500 }))
                : left({ error: new Error(), status: response.status })
        )
        .catch(() => left({ error: new Error(), status: 503 }));

const TRANSLATOR_LANGUAGE_CODE = 'la_LA';

const asProfile = (profileJson: jsonProfile): Profile =>
    new Profile(
        profileJson.user.currentLocale,
        profileJson.deployment.settings.localization.date_format,
        profileJson.deployment.settings.localization.grouping_separator,
        profileJson.deployment.settings.localization.decimal_separator,
        profileJson.location.id,
        profileJson.location.name,
        profileJson.deployment.settings.localization.currency_symbol_in_last_position,
        profileJson.custom_domain.id,
        profileJson.location.type,
        // Limitation of 5 tags on frontend until is limited on platform
        profileJson.selected_location_tags.length > 5 ? profileJson.selected_location_tags.slice(0, 5) : profileJson.selected_location_tags,
        profileJson.deployment.settings.resolutions,
        profileJson.deployment.id,
        profileJson.user.id,
        // Convert available languages to Language entities (i.e. go from snake_case to camelCase)
        profileJson.available_languages
            ? profileJson.available_languages.filter((language) => language.locale_code !== TRANSLATOR_LANGUAGE_CODE).map((language) => new Language(language.name, language.locale_code))
            : []
    );

type jsonProfile = {
    user: {
        currentLocale: string;
        id: string;
    };
    deployment: {
        id: number;
        settings: {
            localization: {
                ['decimal_separator']: string;
                ['grouping_separator']: string;
                ['date_format']: string;
                ['currency_symbol_in_last_position']: boolean;
            };
            resolutions: string[];
        };
    };
    location: {
        id: string;
        name: string;
        type: LocationType;
    };
    ['selected_location_tags']: string[];
    ['custom_domain']: {
        id: number;
    };
    ['available_languages']?: {
        name: string;
        ['locale_code']: string;
    }[];
};
